import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { App, NavController, NavParams, Toast, ToastController } from 'ionic-angular';
import { ModalDirective } from 'ngx-bootstrap';
import { Storage } from '../../../node_modules/@ionic/storage';
import { FileModel } from '../../app/models/FileModel';
import { PricelistProduct } from '../../app/models/PriceListProduct';
import { PriceList } from '../../app/models/Pricelist';
import { Product } from '../../app/models/Product';
import { ProductCategory } from '../../app/models/ProductCategory';
import { TaxRate } from '../../app/models/TaxRate';
import { UnitOfMeasure } from '../../app/models/UnitOfMeasure';
import { NavControllerService } from '../../providers/nav-controller/navControllerService';
import { PaytronBackendService } from '../../providers/paytron-backend/paytron-backend.service';
import { ProductsService } from '../../services/products.service';
import { TableService } from '../../services/table.service';
import { GenericQueryParams } from '../../utils/utils';
import { productsTableConfig } from './productsTableConfig';
import { toLoadingStateStream } from '../../utils/toLoadingStateStream';
import { tap } from 'rxjs/operators';
import { AlertsHolder } from '../../app/models/AlertsHolder';

@Component({
  selector: 'page-products',
  templateUrl: 'products.html',
})
export class ProductsPage implements OnInit {
  @ViewChild('deleteModal')
  private confirmModal: ModalDirective;

  @ViewChild('productModal')
  private productModal: ModalDirective;

  @ViewChild('productForm')
  private productForm: NgForm;

  @ViewChild('priceListModal')
  private priceListModal: ModalDirective;

  @ViewChild('priceListForm')
  private priceListForm: NgForm;

  @ViewChild('fileInput')
  private fileInput: ElementRef;

  private pageName: string = 'page-products';
  private products: Product[] = [];
  private currentProduct = new Product();
  private currentPriceListProduct = new PricelistProduct();
  private priceLists: PriceList[] = [];
  private taxrates: TaxRate[] = [];
  private unitsOfMeasure: UnitOfMeasure[] = [];
  private openPriceListModal = false;
  private priceListsByProduct: PricelistProduct[] = [];
  private productCategories: ProductCategory[] = [];
  private navControllerService: NavControllerService;
  private selectedFile: File;
  private currentCodeError: string = null;
  public tableHeaders = productsTableConfig;
  public isLoading = false;
  private alertsHolder: AlertsHolder;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private app: App,
    private translate: TranslateService,
    private backendService: PaytronBackendService,
    private productsService: ProductsService,
    private storage: Storage,
    private tableService: TableService
  ) {
    this.navControllerService = new NavControllerService(app, storage);
    this.alertsHolder = new AlertsHolder(translate);
  }

  ngOnInit(): void {
    this.productModal.config = {
      ignoreBackdropClick: true,
      backdrop: 'static',
      keyboard: false,
    };
    this.loadProductCategories();
    this.loadPriceList();
    this.loadTaxRates();
    this.loadUnitsOfMeasure();
  }

  onCodeChange(event) {
    const errorArray = this.products.filter((product) => product.code === event.target.value);
    if (errorArray.length > 0) {
      this.currentCodeError = 'product code already exist';
    } else {
      this.currentCodeError = null;
    }
  }

  compareModels(item1, item2): boolean {
    return item1 && item2 ? item1.uuid === item2.uuid : item1 === item2;
  }

  productsQueryFn() {
    return (params: GenericQueryParams) => this.productsService.getPaginatedProducts(params);
  }

  private loadProductCategories() {
    this.backendService.getProductCategories().subscribe(
      (categories) => {
        this.productCategories = categories;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadTaxRates() {
    this.backendService.getTaxRates().subscribe(
      (taxrates) => {
        this.taxrates = taxrates;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadUnitsOfMeasure() {
    this.backendService.getUnitsOfMeasure().subscribe(
      (unitsOfMeasure) => {
        this.unitsOfMeasure = unitsOfMeasure;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  private loadPriceList() {
    this.backendService.getPriceLists().subscribe(
      (pricelist) => {
        this.priceLists = pricelist;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  addProduct() {
    this.currentProduct = new Product();
    this.fileInput.nativeElement.value = '';
    this.productForm.resetForm();
    this.showProductModal();
  }

  editProduct(product: Product) {
    this.fileInput.nativeElement.value = '';
    this.currentProduct = product;
    this.showProductModal();
  }

  submitAndEditPriceList(btnEvent: HTMLButtonElement) {
    this.openPriceListModal = true;
    this.submitProduct(btnEvent);
  }

  submitProduct(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.productsService
      .saveOrUpdateProduct(this.currentProduct)
      .subscribe(
        (product) => {
          if (this.openPriceListModal) {
            this.editProductList(product);
          }
          this.tableService.refetch();
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        btnEvent.disabled = false;
        this.openPriceListModal = false;
        this.closeProductModal();
      });
  }

  submitPriceList(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.checkIfPriceListProductExists();
    this.backendService
      .saveOrUpdatePriceListProduct(this.currentPriceListProduct)
      .subscribe(
        (data) => {
          this.getPriceListsByProduct(this.currentProduct);
          this.priceListForm.resetForm();
        },
        (err: HttpErrorResponse) => {
          this.closePriceListModal();
        }
      )
      .add(() => {
        btnEvent.disabled = false;
      });
  }

  checkIfPriceListProductExists() {
    this.priceListsByProduct.forEach((item) => {
      if (item.priceList.uuid == this.currentPriceListProduct.priceList.uuid) {
        this.currentPriceListProduct.uuid = item.uuid;
      }
    });
  }

  deleteFromPriceList(priceListProduct) {
    this.backendService.deletePriceListProduct(priceListProduct).subscribe(
      (data) => {
        this.getPriceListsByProduct(this.currentProduct);
      },
      (err: HttpErrorResponse) => {
        this.closeConfirmModal();
      }
    );
  }

  deleteProduct() {
    this.productsService
      .deleteProduct(this.currentProduct)
      .pipe(toLoadingStateStream)
      .subscribe((data) => {
        this.isLoading = data.state === 'loading';
        switch (data.state) {
          case 'loaded':
            this.tableService.refetch();
            this.closeConfirmModal();
            break;
          case 'error':
            this.alertsHolder.show('Cannot delete this product', 'danger');
            break;
        }
      });
  }

  editProductList(product: Product) {
    this.currentPriceListProduct = new PricelistProduct();
    this.currentPriceListProduct.product = product;
    this.currentProduct = product;
    this.priceListForm.resetForm();
    this.currentPriceListProduct.price = product.price;
    this.showPriceListModal();
    this.getPriceListsByProduct(product);
  }

  private getPriceListsByProduct(product: Product) {
    this.backendService.getPriceListsByProduct(product).subscribe(
      (data) => {
        this.priceListsByProduct = data;
      },
      (err: HttpErrorResponse) => {
        this.closeConfirmModal();
      }
    );
  }

  onFileChanged(event) {
    if (event.target.files[0]) {
      this.selectedFile = event.target.files[0];
      this.uploadData();
    }
  }

  uploadData() {
    const uploadData = new FormData();
    uploadData.append('files', this.selectedFile, this.selectedFile.name);
    this.backendService.addFile(uploadData).subscribe(
      (data: FileModel) => {
        this.currentProduct.icon = data.name;
      },
      (err: HttpErrorResponse) => {}
    );
  }

  showConfirmModal(product) {
    this.currentProduct = product;
    this.confirmModal.show();
  }

  closeConfirmModal() {
    this.confirmModal.hide();
  }

  showProductModal() {
    this.productModal.show();
  }

  closeProductModal() {
    this.productModal.hide();
  }

  showPriceListModal() {
    this.priceListModal.show();
  }

  closePriceListModal() {
    this.priceListModal.hide();
  }

  showProductCategoryPage() {
    this.navControllerService.showProductCategoriesPage();
  }

  getTitle(msg: string) {
    return this.translate.instant(msg);
  }
}



// WEBPACK FOOTER //
// ./src/pages/products/products.ts